import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useQuery } from '@tanstack/react-query';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { adminFinanceApi, adminFinanceKeys } from '@/features/admin-finance/api/admin-finance.api';
import { formatCurrency } from '@/features/guide-finance/utils/formatCurrency';
import {
  financeChartSx,
  financeSectionTitleSx,
} from '@/features/admin-finance/styles/financeSurfaces';

type OverviewPanelProps = {
  onNavigateTab?: (tab: string) => void;
};

export function FinanceOverviewPanel({ onNavigateTab }: OverviewPanelProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: adminFinanceKeys.analytics({ days: 30 }),
    queryFn: () => adminFinanceApi.getAnalytics({ days: 30 }),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error || !data) return <Alert severity="error" sx={{ borderRadius: 0 }}>Failed to load finance analytics.</Alert>;

  const { summary, settlementSeries, withdrawalStatusBreakdown, settlementTotals } = data;

  const balancePie = [
    { id: 0, value: summary.totalPending, label: 'Pending', color: '#CA8A04' },
    { id: 1, value: summary.totalAvailable, label: 'Available', color: '#0F766E' },
    { id: 2, value: summary.totalWithdrawn, label: 'Withdrawn', color: '#64748B' },
  ].filter((item) => item.value > 0);

  const kpiCards = [
    { label: 'Pending balances', value: formatCurrency(summary.totalPending), tab: 'wallets' },
    { label: 'Available balances', value: formatCurrency(summary.totalAvailable), tab: 'wallets' },
    { label: 'Total withdrawn', value: formatCurrency(summary.totalWithdrawn), tab: 'withdrawals' },
    { label: 'Lifetime earnings', value: formatCurrency(summary.totalEarnings), tab: 'settlements' },
    {
      label: 'Pending withdrawals',
      value: String(summary.pendingWithdrawals),
      tab: 'withdrawals',
    },
    {
      label: 'Platform commission',
      value: formatCurrency(settlementTotals.totalPlatform),
      tab: 'settlements',
    },
  ];

  return (
    <Box>
      <Typography sx={financeSectionTitleSx}>Key metrics</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
          gap: '1px',
          bgcolor: 'divider',
          border: '1px solid',
          borderColor: 'divider',
          mb: 3,
        }}
      >
        {kpiCards.map((card) => (
          <Box
            key={card.label}
            onClick={() => onNavigateTab?.(card.tab)}
            sx={{
              p: 2.5,
              bgcolor: 'background.paper',
              cursor: onNavigateTab ? 'pointer' : 'default',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(26, 46, 26, 0.04)' },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: '0.08em',
                fontSize: '0.65rem',
              }}
            >
              {card.label}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mt: 0.75,
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: 'text.primary',
              }}
            >
              {card.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography sx={financeSectionTitleSx}>Analytics</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Box sx={financeChartSx}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Balance mix
            </Typography>
            {balancePie.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 0 }}>
                No wallet balances yet.
              </Alert>
            ) : (
              <PieChart
                height={240}
                series={[
                  {
                    data: balancePie,
                    innerRadius: 48,
                    outerRadius: 90,
                    paddingAngle: 1,
                    cornerRadius: 0,
                  },
                ]}
              />
            )}
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Box sx={financeChartSx}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Settlements · last {data.days} days
            </Typography>
            <LineChart
              height={240}
              xAxis={[
                {
                  data: settlementSeries.map((s) => s.date),
                  scaleType: 'point',
                  tickLabelStyle: { fontSize: 10 },
                },
              ]}
              series={[
                { data: settlementSeries.map((s) => s.grossAmount), label: 'Gross', color: '#1A2E1A' },
                {
                  data: settlementSeries.map((s) => s.platformAmount),
                  label: 'Platform',
                  color: '#C8A96E',
                },
                { data: settlementSeries.map((s) => s.guideAmount), label: 'Guide', color: '#0F766E' },
              ]}
              margin={{ left: 56, right: 16, top: 16, bottom: 36 }}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box sx={financeChartSx}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Withdrawal pipeline
            </Typography>
            {withdrawalStatusBreakdown.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 0 }}>
                No withdrawal requests yet.
              </Alert>
            ) : (
              <BarChart
                height={260}
                borderRadius={0}
                xAxis={[
                  {
                    data: withdrawalStatusBreakdown.map((w) => w.status),
                    scaleType: 'band',
                  },
                ]}
                series={[
                  {
                    data: withdrawalStatusBreakdown.map((w) => w.count),
                    label: 'Count',
                    color: '#1A2E1A',
                  },
                  {
                    data: withdrawalStatusBreakdown.map((w) => w.amount),
                    label: 'Amount',
                    color: '#C8A96E',
                  },
                ]}
                margin={{ left: 56, right: 16, top: 28, bottom: 36 }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
